
import Vue, { PropType } from 'vue'
import * as t from '@/store/chisai/GCB2/actionTypes'
import TextField from '../../../inputs/TextField.vue'
import { ACTIVATION_DESCRIPTION_MAX_LENGTH, ACTIVATION_NAME_MAX_LENGTH } from '../../../../vars/GCB2'
import groupBy from 'lodash/groupBy'
import first from 'lodash/first'
import cloneDeep from 'lodash/cloneDeep'
import random from 'lodash/random'
import uniqueId from 'lodash/uniqueId'
import { Drag, Drop, DropMask } from 'vue-easy-dnd'
import AddMessagePartButton from './AddMessagePartButton.vue'
import SmileSelector from '../../../../components/ui/SmileSelector.vue'
import { Activation, EditedActivation, IGCB2Connector } from '../../../../types/GCB2'
import MessageCollectionButton from './MessageCollectionButton.vue'
import api from '../../../../api/api'
import messages from '@dataheroes/messages'
import CustomActivatorSelect from '../../../inputs/CustomActivatorSelect.vue'
import { Project } from '../../../../types/main'
import { CONNECTOR_TYPES } from '../../../../vars/general'
import cfg from '../../../../../config'
import message from '@root/src/store/chisai/GCB2/message/message'
import SendMessageDialog from '../taskList/SendMessageDialog.vue'

// Переменные с предсказуемой длиной значения — считаются в счётчике по этой длине.
// Остальные переменные длину не задают (hasDynamic), считаются как «+» / «от N SMS».
const SMS_FIXED_LENGTHS: Record<string, number> = {
    '{дата-записи}': 10,
    '{время-записи}': 5,
    '{дата-и-время}': 16,
}

export default Vue.extend({
    components: {
        TextField,
        AddMessagePartButton,
        Drag,
        Drop,
        MessageCollectionButton,
        CustomActivatorSelect,
        SmileSelector,
        SendMessageDialog
    },
    props: {
        value: { type: Object as PropType<EditedActivation> },
        automailValidate: {
            type: Object,
            required: false,
        },
        type: {
            type: String,
            required: false,
        },
        scriptTextPlaceholder: {
            type: String,
            required: false,
            default: '',
        },
        extraMessageVars: {
            type: Array,
            required: false,
            default: () => [],
        },
        previewTextTransform: {
            type: Function,
            required: false,
            default: null,
        },
    },
    data: () => ({
        activePartNumber: 1,
        activeTab: 0,
        nameRules: [
            (v: any) => !!v || 'Обязательно',
            (v: any) =>
                v.length <= ACTIVATION_NAME_MAX_LENGTH || `Меньше ${ACTIVATION_NAME_MAX_LENGTH} символов`,
        ],
        isSendMessageDialog: false,
        activeItem: {} as any
    }),
    watch: {
        isValidScripts(): void {
            this.automailValidate.isValidScripts = this.isValidScripts
        },
    },
    computed: {
        // Подсчёт длины и SMS-сегментов по правилам ФТ:
        // длина в UTF-16 (эмодзи = 2), переменные фикс. длины считаются по своей длине,
        // остальные — обнуляются и помечают hasDynamic. ≤70 → 1 SMS, дальше по 67.
        smsSegmentInfo(): { length: number; segments: number; hasDynamic: boolean } {
            const text = this.value?.message?.smsText || ''
            let hasDynamic = false
            const rendered = text.replace(/\{[^{}]+\}/g, (token: string) => {
                if (SMS_FIXED_LENGTHS[token] !== undefined) {
                    return '0'.repeat(SMS_FIXED_LENGTHS[token])
                }
                hasDynamic = true
                return ''
            })
            const length = rendered.length
            let segments = 0
            if (text.trim()) segments = length <= 70 ? 1 : Math.ceil(length / 67)
            if (hasDynamic && segments === 0) segments = 1
            return { length, segments, hasDynamic }
        },
        smsCharCountText(): string {
            const { length, hasDynamic } = this.smsSegmentInfo
            return `${length}${hasDynamic ? '+' : ''} ${this.pluralRu(
                length,
                'символ',
                'символа',
                'символов'
            )}`
        },
        smsSegmentText(): string {
            const { segments, hasDynamic } = this.smsSegmentInfo
            return `${hasDynamic ? 'от ' : ''}${segments} SMS`
        },
        smsValidation(): { cls: string; text: string } {
            const raw = this.value?.message?.smsText || ''
            if (!raw.trim()) {
                return {
                    cls: 'neutral',
                    text: 'Текст не задан — при отправке будет пропуск SMS-шага каскада',
                }
            }
            if (raw.trim().length < 3) {
                return { cls: 'warn', text: 'Введите минимум 3 символа' }
            }
            return { cls: 'ok', text: 'Текст готов' }
        },
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        allowedMessageVarsIds(): string[] {
            const vars = [
                'client.name',
                'user.name',
                'bot.salonName',
                'bot.salonPhone',
                'bot.salonAddress',
                'bot.feedbackLink',
                'simple.eventDate',
                'simple.onlineRecordLink',
                'simple.headName',
                'simple.adminName',
                'simple.masterName',
                'simple.giftName',
                'simple.serviceName',
            ]
            if (cfg.audienceWithReturnAfterVar.includes(this.value.audienceId!)) {
                vars.push('returnAfter')
            }
            return vars
        },
        GCB2Connector(): IGCB2Connector {
            return this.project.connectors.find(
                el => el.connectorType === CONNECTOR_TYPES.GCB2
            ) as IGCB2Connector
        },
        messagePartsByPartNumber(): any[] {
            const groupedMessageParts = groupBy(
                this.value.message.messageParts.filter(el => !el._deleted),
                'partNumber'
            )
            return Object.keys(groupedMessageParts).map(partNumber => ({
                partNumber: Number(partNumber),
                messageParts: groupedMessageParts[partNumber],
            }))
        },
        activeMessagePartsByPartNumber(): any[] {
            return (
                this.messagePartsByPartNumber.find(el => el.partNumber === this.activePartNumber)
                    ?.messageParts || []
            )
        },
        messageVarsList(): any[] {
            const base = messages.vars.vars
                .filter(el => this.allowedMessageVarsIds.includes(el.id))
                .map(el => ({ id: first(el.aliases), name: first(el.aliases) }))
            const extra = (this.extraMessageVars || []).map((item: any) => {
                const id = String(item?.id || item || '')
                return { id, name: String(item?.name || id) }
            })
            return [...base, ...extra]
        },
        showMessageCollection(): Boolean {
            return this.GCB2Connector.showMessageCollection
        },
        isValidScripts(): any {
            const messageParts = this.value?.message?.messageParts?.filter(el => !el._deleted)
            return messageParts.length > 0 && messageParts.every(el => el?.textVariant?.trim()?.length > 2)
        },
        disableGenerateButton(): boolean {
            return this.messagePartsByPartNumber?.every(el => {
                const exp1 = el.messageParts?.every(v => v?.textVariant === el.messageParts[0]?.textVariant)
                const exp2 = el.messageParts?.length < 2
                return exp1 || exp2
            })
        }
    },
    methods: {
        currentPartClass(messageParts) {
            if (this.activePartNumber === messageParts.partNumber) {
                return 'active'
            } else if (
                this.automailValidate.isAutomail &&
                this.automailValidate.isSaveButtonClicked &&
                messageParts.messageParts.some(el => el.textVariant.trim().length < 3)
            ) {
                return 'error-part'
            }
        },
        insertVar(messagePart: any, varALias: string, textareaIndex: number) {
            const textareas = this.$refs['message-textareas'] as any[]
            const el = textareas[textareaIndex].$refs.input
            const [start, end] = [el.selectionStart, el.selectionEnd]
            messagePart.textVariant = [
                messagePart.textVariant.slice(0, start),
                ` {${varALias}}`,
                messagePart.textVariant.slice(start),
            ].join('')
        },

        // Вообще думаю стоит сделать общую функцию для этой, и функции выше
        // но пока не буду во избежание неприятностей
        insertEmoji(messagePart: any, emoji: string, textareaIndex: number) {
            const textareas = this.$refs['message-textareas'] as any[]
            const el = textareas[textareaIndex].$refs.input
            const [start, end] = [el.selectionStart, el.selectionEnd]
            messagePart.textVariant = [
                messagePart.textVariant.slice(0, start),
                `${emoji}`,
                messagePart.textVariant.slice(start),
            ].join('')
        },

        pluralRu(n: number, one: string, few: string, many: string): string {
            const m10 = n % 10
            const m100 = n % 100
            if (m10 === 1 && m100 !== 11) return one
            if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
            return many
        },
        insertAtSmsCursor(insertText: string) {
            const el = this.$refs['sms-textarea'] as HTMLTextAreaElement | undefined
            const cur = this.value.message.smsText || ''
            const start = el && el.selectionStart != null ? el.selectionStart : cur.length
            this.value.message.smsText = cur.slice(0, start) + insertText + cur.slice(start)
            this.$nextTick(() => {
                if (!el) return
                const pos = start + insertText.length
                el.focus()
                el.setSelectionRange(pos, pos)
            })
        },
        insertVarSms(varAlias: string) {
            this.insertAtSmsCursor(` {${varAlias}}`)
        },
        insertEmojiSms(emoji: string) {
            this.insertAtSmsCursor(emoji)
        },
        clearSms() {
            this.value.message.smsText = ''
            this.$nextTick(() => {
                const el = this.$refs['sms-textarea'] as HTMLTextAreaElement | undefined
                el && el.focus()
            })
        },
        openSmsPreview() {
            this.activeItem = {
                textMessage: this.value.message.smsText || '',
                activationMedia: this.value?.media,
            }
            if (this.activeItem?.textMessage) {
                this.activeItem = this.finalizePreviewItem(this.activeItem)
            }
            this.isSendMessageDialog = true
        },
        formInput(e: any) {
            this.$emit('validation-updated', e)
        },
        clear() {
            ;(this.$refs as any).form.resetValidation()
            this.activePartNumber = 1
        },
        addMessagePart(partNumber: number) {
            const newMessageParts = [...this.value.message.messageParts].concat([
                { partNumber, textVariant: '', _id: uniqueId() },
            ])
            this.activePartNumber = partNumber
            this.$emit(
                'input',
                Object.assign({}, this.value, {
                    message: {
                        ...this.value.message,
                        messageParts: newMessageParts,
                    },
                })
            )
        },
        deleteMessagePart(messagePart: any) {
            const message = this.value.message
            const newMessageParts = [...message.messageParts]
            const messageToDelete = messagePart._id ? newMessageParts.find((el: any) => el._id === messagePart._id) : newMessageParts.find((el: any) => el.id === messagePart.id)
            const messagePartsWithThisPartNumber =
                this.messagePartsByPartNumber.find(el => el.partNumber === messagePart.partNumber)
                    .messageParts || []
            if (messagePartsWithThisPartNumber.length === 1) {
                //части сообщений, у которых номер части больше чем номер части удаляемой
                const messagePartsWithPartNumberBiggerThanThis = newMessageParts.filter(
                    el => el.partNumber > messagePart.partNumber
                )
                messagePartsWithPartNumberBiggerThanThis.forEach(el => {
                    el.partNumber = el.partNumber - 1
                })
            }
            messageToDelete!._deleted = true
            this.$emit(
                'input',
                Object.assign({}, this.value, {
                    message: {
                        ...this.value.message,
                        messageParts: newMessageParts,
                    },
                })
            )
        },
        setMessage(_message: any) {
            const currentMessage = this.value.message
            const message = cloneDeep(_message)
            message.id = currentMessage.id
            message.messageParts = currentMessage.messageParts
                .map(el => ({ ...el, _deleted: true }))
                .concat(message.messageParts)
            this.$emit(
                'input',
                Object.assign({}, this.value, {
                    message,
                })
            )
        },
        updateMessagePartPartNumber(e: any, newPartNumber: number) {
            const messagePart = e.data
            const message = this.value.message
            const newMessageParts = [...message.messageParts]
            const indexToUpdate = newMessageParts.findIndex((el: any) => el._id === messagePart._id)
            const messagePartsWithThisPartNumber =
                this.messagePartsByPartNumber.find(el => el.partNumber === messagePart.partNumber)
                    .messageParts || []
            const updateMessagePart = () => {
                newMessageParts.splice(
                    indexToUpdate,
                    1,
                    Object.assign({}, messagePart, { partNumber: newPartNumber })
                )
            }
            if (messagePartsWithThisPartNumber.length === 1) {
                if (messagePart.partNumber > newPartNumber) {
                    const messagePartsWithPartNumberBiggerThanThis = newMessageParts.filter(
                        el => el.partNumber > messagePart.partNumber
                    )
                    messagePartsWithPartNumberBiggerThanThis.forEach(el => {
                        el.partNumber = el.partNumber - 1
                    })
                    updateMessagePart()
                } else {
                    updateMessagePart()
                    const messagePartsWithPartNumberBiggerThanThis = newMessageParts.filter(
                        el => el.partNumber > messagePart.partNumber
                    )
                    messagePartsWithPartNumberBiggerThanThis.forEach(el => {
                        el.partNumber = el.partNumber - 1
                    })
                }
            } else {
                updateMessagePart()
            }

            this.$emit(
                'input',
                Object.assign({}, this.value, {
                    message: {
                        ...this.value.message,
                        messageParts: newMessageParts,
                    },
                })
            )
        },
        finalizePreviewItem(item) {
            if (typeof this.previewTextTransform !== 'function' || !item?.textMessage) {
                return item
            }
            return {
                ...item,
                textMessage: this.previewTextTransform(item.textMessage),
            }
        },
        openPreview() {
            this.generateRandomScript()
            if (this.activeItem?.textMessage) {
                this.activeItem = this.finalizePreviewItem(this.activeItem)
            }
            this.isSendMessageDialog = true
        },
        generateRandomScript() {
            const randomArr = []
            let finalString = ''
            if (this.disableGenerateButton) {
                this.messagePartsByPartNumber.forEach(el => randomArr.push(el.messageParts[0].textVariant))
                finalString = randomArr.join(' ')
                this.activeItem = {
                    textMessage: finalString,
                    activationMedia: this.value?.media
                }
            } else {
                for(let i = 0; i < this.messagePartsByPartNumber.length; i++) {
                    const randNumber = random(0, (this.messagePartsByPartNumber[i].messageParts.length - 1))
                    randomArr.push(this.messagePartsByPartNumber[i].messageParts[randNumber].textVariant)
                }
                finalString = randomArr.join(' ')
                if(this.activeItem?.textMessage === finalString) {
                    this.generateRandomScript()
                    return
                }
                this.activeItem = {
                    textMessage: finalString,
                    activationMedia: this.value?.media
                }
            }
            if (this.activeItem?.textMessage) {
                this.activeItem = this.finalizePreviewItem(this.activeItem)
            }
        }
    },
    async created() {},
    mounted() {
        this.automailValidate.isValidScripts = this.isValidScripts
    },
})
